import {unwrapResult} from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import {PostParams} from '~/utils/thunk-api/type';
import {requestFailed, requestOK, ThunkApi} from '~/utils/thunk-api';
import {AuthApiItem, AuthEntity, NormalizedAuth} from '~/redux/auth/types';
import {ENDPOINTS} from '~/constants';
import {removeTokenRequestHeader, setTokenRequestHeader} from '~/utils/request';
import * as storage from '~/utils/storage';
import {AppThunk} from '~/redux/root-store';
import {setCurrentUser} from '~/redux/auth/slice';
import {authSchema} from '~/redux/auth/entity-config';
import AppError from '~/utils/error-handler';
import {fetchCurrentUserInfo} from '~/redux/admins/thunk';
import {saveUserCredential} from '~/services/auth';

export interface LoginByEmailParams extends PostParams {
  data: {
    email: string;
    password: string;
  };
}

export interface SocialLoginParams extends PostParams {
  data: {
    provider: string;
    token: string;
    clientId: string;
    name?: string;
    preferredName?: string;
  };
}

const thunk = new ThunkApi<AuthEntity, NormalizedAuth>('auth', authSchema);

export const onLoginSuccessMisc = thunk.misc<AuthEntity>('onLoginSuccess', async ({token, user}, dispatch, onError) => {
  try {
    let refreshUser = user;
    setTokenRequestHeader(token);
    const resAction = await dispatch(fetchCurrentUserInfo({}));
    if (requestFailed(resAction, fetchCurrentUserInfo)) {
      return onError();
    }
    const {apiResponseData} = unwrapResult(resAction);
    refreshUser = apiResponseData;

    await saveUserCredential({user, accessToken: token});

    await dispatch(setCurrentUser({user: refreshUser, token}));
    // @ts-ignore
    Sentry.setUser(refreshUser);
    Sentry.setTag('email', refreshUser?.email);
  } catch (e) {
    onError(new AppError(e as any));
  }
  return true;
});

export const loginByEmail = thunk.post<LoginByEmailParams>(ENDPOINTS.LOGIN, 'byEmailReq', {
  includeApiResponseData: true,
});
export const socialLogin = thunk.post<SocialLoginParams>(ENDPOINTS.SOCIAL_LOGIN, 'bySocialReq', {
  includeApiResponseData: true,
});

const parseAuthApiData = (resAction: any): AuthApiItem => {
  const {apiResponseData} = unwrapResult(resAction);
  return apiResponseData;
};

export const loginByEmailMisc = thunk.misc<LoginByEmailParams>(
  'loginByEmailMisc',
  async (params, dispatch, onError) => {
    const resAction = await dispatch(loginByEmail(params));
    if (requestOK(resAction, loginByEmail)) {
      const authApiItem = parseAuthApiData(resAction);
      await dispatch(onLoginSuccessMisc(authApiItem));
      return resAction;
    }
    return onError();
  },
);

export const socialLoginMisc = thunk.misc<SocialLoginParams>('loginBySocial', async (params, dispatch, onError) => {
  const resAction = await dispatch(socialLogin(params));
  if (requestOK(resAction, socialLogin)) {
    const authApiItem = parseAuthApiData(resAction);
    await dispatch(onLoginSuccessMisc(authApiItem));
    return resAction;
  }
  return onError();
});

export const logout = (): AppThunk => async dispatch => {
  await storage.remove('EASE_WEB_MEDICAL_USER');
  await storage.remove('EASE_WEB_MEDICAL_ACCESS_TOKEN');
  removeTokenRequestHeader();
  dispatch({type: 'USER_LOGOUT'});
};
